import type { FetchByQueryResult, Segment } from "mobx-repository";
import { prop } from "ramda";
import type {
    PageInfo,
    RoutesStationaryUnitsListStationaryUnitsRequest,
    StationaryUnit,
    StationaryUnitCreate,
    StationaryUnitsApi,
    StationaryUnitSortKey,
    StationaryUnitUpdate,
} from "src/api";
import { defaultPageSize } from "src/utils/constants";
import { ElofleetRepository, type ApiResource } from "src/utils/elofleet-repository";
import type { UUID } from "src/utils/uuid";

export type StationaryUnitsQuery = Omit<RoutesStationaryUnitsListStationaryUnitsRequest, "page">;

export interface StationaryUnitsApiResource extends ApiResource {
    entity: StationaryUnit;
    query: StationaryUnitsQuery;
    update: StationaryUnitUpdate;
    create: StationaryUnitCreate;
    sortKey: StationaryUnitSortKey;
}

export class RepositoryStationaryUnits extends ElofleetRepository<StationaryUnitsApiResource> {
    constructor(private readonly stationaryUnitsApi: StationaryUnitsApi) {
        super();
    }

    protected extractId = prop("id");

    protected async fetchById(stationaryUnitId: string): Promise<StationaryUnit | undefined> {
        return await this.stationaryUnitsApi.routesStationaryUnitsReadStationaryUnit({
            stationaryUnitId,
        });
    }

    protected async fetchByQuery(
        query: StationaryUnitsQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<StationaryUnit>> {
        const page = pagination.offset / this.defaultCount;
        const entities = await this.stationaryUnitsApi.routesStationaryUnitsListStationaryUnits({
            ...query,
            page,
        });
        return { entities };
    }

    public fetchMetadata(query: StationaryUnitsQuery): Promise<PageInfo> {
        return this.stationaryUnitsApi.routesStationaryUnitsListStationaryUnitsMetadata(query);
    }

    public create(): Promise<StationaryUnit> {
        throw new Error("Cannot create stationary units: Not implemented.");
    }

    public update(): Promise<StationaryUnit> {
        throw new Error("Cannot update stationary units: Not implemented.");
    }

    public async delete(...stationaryUnitIds: UUID[]): Promise<void> {
        await Promise.all(
            stationaryUnitIds.map((stationaryUnitId) =>
                this.stationaryUnitsApi.routesStationaryUnitsDeleteStationaryUnit({
                    stationaryUnitId,
                }),
            ),
        );

        // This reload is necessary to reload all queries that want to display "all" entities.
        await this.waitForIdle();
        this.reloadQuery({ pageSize: defaultPageSize });
    }
}
